import { service } from '@ember/service';
import Route from '@ember/routing/route';
import { findRecord, query } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store as StoreService } from '@warp-drive/core';

export default class AuthenticatedAdminPslfFormReviewRoute extends Route {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare router: RouterService;

  async model(params: { form_id: string }) {
    const { form_id } = params;

    const { content: formContent } = (await this.store.request(
      findRecord<PslfFormModel>('pslf-form', form_id, {
        include: 'attachments,employee',
        reload: true,
      })
    )) as { content: { data: PslfFormModel } };
    const form = formContent.data;

    const approver = this.sessionContext.currentEmployee;
    const { content: approverContent } = (await this.store.request(
      findRecord<EmployeeModel>('employee', approver.id!, {
        include: 'pslf-profile,employee-addition',
        reload: true,
      })
    )) as { content: { data: EmployeeModel } };
    const reloadedApprover = approverContent.data;

    let profile = reloadedApprover.pslfProfile;
    if (!profile) {
      profile = this.store.createRecord<PslfProfileModel>('pslf-profile', {
        employee: reloadedApprover,
      });
      await saveRecord(this.store, profile);
    }

    const [awaitingApprovalContent, approvedContent] = await Promise.all([
      this.store.request(
        query<PslfFormModel>('pslf-form', {
          'filter[employee-id]': form.employee.id,
          'filter[status]': 'borrower_signed',
          'page[limit]': 5,
          'page[offset]': 0,
          sort: '-created-at',
        })
      ) as Promise<{ content: { data: PslfFormModel[] } }>,
      this.store.request(
        query<PslfFormModel>('pslf-form', {
          'filter[employee-id]': form.employee.id,
          'filter[status]':
            'employer_signed,awaiting_submit,submitted,submit_errored,re_submit_errored,re_submitted',
          'page[limit]': 50,
          'page[offset]': 0,
          sort: '-created-at',
        })
      ) as Promise<{ content: { data: PslfFormModel[] } }>,
    ]);

    const formsAwaitingApproval = awaitingApprovalContent.content.data
      .filter((f) => f.id !== form.id)
      .slice();
    const formsApproved = approvedContent.content.data.slice();

    return {
      form,
      formsAwaitingApproval,
      formsApproved,
      employee: reloadedApprover,
    };
  }

  afterModel(model: { form: PslfFormModel; employee: EmployeeModel }) {
    if (model.form.status !== 'borrower_signed') {
      this.router.transitionTo('authenticated.admin.pslf');
    }
  }
}
